import type { ReactElement } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TransformWrapper } from 'react-zoom-pan-pinch';

import { Modal } from '@highlight-cp/core-ui';

import AssetGalleryImage from '../asset-gallery-image';
import type { AssetGalleryModalProps } from '../types';

/**
 * ### AssetGalleryModal
 *
 * @description
 * The zoomable, pannable lightbox behind `AssetGallery`, for callers that open
 * it from their own trigger (e.g. clicking the image itself). Steps through
 * `assets` with Previous / Next when there's more than one. Each time it opens
 * it starts on `initialAssetId` (the first asset when that's absent or unknown),
 * and it titles the dialog with the asset on show.
 *
 * @param props - The assets, which one to open on, and the open state
 * @returns The lightbox modal
 */
const AssetGalleryModal = ({
  assets,
  initialAssetId,
  opened,
  onClose,
}: AssetGalleryModalProps): ReactElement => {
  const { t } = useTranslation();
  const initialIndex = Math.max(
    assets.findIndex((asset) => asset.id === initialAssetId),
    0,
  );
  const [currentAssetIndex, setCurrentAssetIndex] = useState(initialIndex);
  const [wasOpened, setWasOpened] = useState(opened);

  // Start over on each open — adjusting state during render rather than in an effect, so the
  // first frame never shows the asset the last session ended on.
  if (opened !== wasOpened) {
    setWasOpened(opened);
    if (opened) setCurrentAssetIndex(initialIndex);
  }

  // `assets` can shrink under a stored index; clamp rather than render nothing.
  const currentAsset = assets[Math.min(currentAssetIndex, assets.length - 1)];

  return (
    // Z-index is set to 201 to ensure the modal is above the preview modal
    <Modal
      onClose={onClose}
      opened={opened && Boolean(currentAsset)}
      size="xl"
      title={currentAsset?.alt || currentAsset?.name || t('Asset image')}
      zIndex={201}
    >
      {currentAsset && (
        <TransformWrapper>
          <AssetGalleryImage
            assets={assets}
            currentAsset={currentAsset}
            setCurrentAssetIndex={setCurrentAssetIndex}
          />
        </TransformWrapper>
      )}
    </Modal>
  );
};

export default AssetGalleryModal;
